import CountriesDropdown from 'components/Common/countries-dropdown';
import React, { useState, useEffect } from 'react';
import { PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import { downloadAnalytics } from 'services/downloadAnalytics';

const PublicPolicyDownloadsDashboard = () => {
  // State for filters
  const [dateRange, setDateRange] = useState('past_30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [countryId, setCountryId] = useState('');
  const [isAgraSupported, setIsAgraSupported] = useState(null);
  const [dateError, setDateError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // State for data
  const [dashboardData, setDashboardData] = useState({
    total_downloads: null,
    average_downloads_per_week: null,
    average_downloads_per_month: null,
    average_downloads_per_year: null,
    downloads_by_reason: {
      labels: [],
      counts: []
    },
    downloads_by_interest_group: {
      labels: [],
      counts: []
    },
    top_policies: {
      labels: [],
      counts: []
    },
    top_systems: {
      labels: [],
      counts: []
    }
    // total_downloads: 100,
    // average_downloads_per_week: 40,
    // average_downloads_per_month: 60,
    // average_downloads_per_year: 70,
    // downloads_by_reason: {
    //   labels: ["Reason 1", "Reason 2", "Reason 3", "Reason 4"],
    //   counts: [40, 25, 20, 15]
    // },
    // downloads_by_interest_group: {
    //   labels: ["Interest Group 1", "Interest Group 2", "Interest Group 3", "Interest Group 4"],
    //   counts: [45, 25, 20, 10]
    // },
    // top_policies: {
    //   labels: ["Policy A", "Policy B", "Policy C", "Policy D", "Policy E"],
    //   counts: [120, 98, 85, 72, 31]
    // },
    // top_systems: {
    //   labels: ["Fertilizer", "Seeds", "Irrigation", "Mechanization", "Livestock"],
    //   counts: [59, 52, 43, 37, 29]
    // }
  });
  
  // Colors for charts
  const barColor = '#036f36';
  const pieColors = ['#036f36', '#0A9847', '#43D692', '#65E2AB', '#18693B', '#0E7A3F', '#27754C'];
  
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    return percent > 0.1 ? (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };
  
  // Fetch data function
  const fetchData = async () => {

    const params = new URLSearchParams();
    params.append('date_range', dateRange);
    if (dateRange === 'custom_time_range') {
      params.append('custom_start_date', new Date(customStartDate).toISOString());
      params.append('custom_end_date', new Date(customEndDate).toISOString());
    }
    if (countryId) params.append('country_id', countryId);
    if (isAgraSupported !== null) params.append('is_agra_supported', isAgraSupported);
    
    try {
        const  data  = await downloadAnalytics(params.toString());
        setDashboardData(data);
    } catch (err) {
        console.error('Error loading data:', err);
    } 
  };
  
  useEffect(() => {
    fetchData();
  }, [
    // dateRange, countryId, isAgraSupported, customStartDate, customEndDate
]);

  // Generate data for charts
  const reasonData = dashboardData?.downloads_by_reason?.labels?.map((label, index) => ({
    name: label,
    downloads: dashboardData?.downloads_by_reason?.counts[index]
  }));
  
  const interestGroupData = dashboardData?.downloads_by_interest_group?.labels?.map((label, index) => ({
    name: label,
    value: dashboardData?.downloads_by_interest_group?.counts[index]
  }));
  
  // Handle date range change
  const handleDateRangeChange = (e) => {
    setDateRange(e.target.value);
  };
  
  // Handle custom date changes
  const handleStartDateChange = (e) => {
    setCustomStartDate(e.target.value);
  };
  
  const handleEndDateChange = (e) => {
    setCustomEndDate(e.target.value);
  };
  
  // Validate date range
  const validateDateRange = () => {

    // Clear previous errors
    setDateError('');

    // Only validate if custom time range is selected
    if (dateRange === 'custom_time_range') {

      // Check if both dates are provided
      if (!customStartDate || !customEndDate) {
        setDateError('Both start and end dates are required');
        return false;
      }
      // Check if end date is not before start date
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);

      if (endDate < startDate) {
        setDateError('End date cannot be before start date');
        return false;
      }
    }

    return true;
  };
  
  // Handle apply filters button
  const handleApplyFilters = () => {
    if(validateDateRange()){
        fetchData();
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-4">Public Policy Downloads Dashboard</h1>
      
      {/* Filters Section */}
      <div className="border border-gray-300 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold mb-2">Filter by Country</h2>
            <div className="border rounded">
                <CountriesDropdown countryID={countryId} setCountryID={setCountryId}/>
            </div>
          </div>
          
          <div>
            <h2 className="text-lg font-semibold mb-2">AGRA Supported</h2>
            <select 
              className="w-full p-2 border rounded"
              value={isAgraSupported === null ? '' : isAgraSupported.toString()}
              onChange={(e) => {
                if (e.target.value === '') {
                  setIsAgraSupported(null);
                } else {
                  setIsAgraSupported(e.target.value === 'true');
                }
              }}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          
          <div>
            <h2 className="text-lg font-semibold mb-2">Filter by Date</h2>
            <select 
              className="w-full p-2 border rounded mb-2"
              value={dateRange}
              onChange={handleDateRangeChange}
            >
              <option value="past_day">Past Day</option>
              <option value="past_7_days">Past 7 Days</option>
              <option value="past_30_days">Past 30 Days</option>
              <option value="past_90_days">Past 90 Days</option>
              <option value="past_6_months">Past 6 Months</option>
              <option value="past_one_year">Past Year</option>
              <option value="custom_time_range">Custom Time Range</option>
            </select>
            
            {dateRange === 'custom_time_range' && (
                <>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                        <label className="block text-sm">Start Date</label>
                        <input 
                            type="date" 
                            className={`w-full p-2 border rounded ${dateError ? 'border-red-500' : ''}`}
                            value={customStartDate}
                            onChange={handleStartDateChange}
                        />
                        </div>
                        <div>
                        <label className="block text-sm">End Date</label>
                        <input 
                            type="date" 
                            className={`w-full p-2 border rounded ${dateError ? 'border-red-500' : ''}`}
                            value={customEndDate}
                            onChange={handleEndDateChange}
                        />
                        </div>
                    </div>
                    {dateError && (
                        <div className="mt-1 text-red-500 text-sm">{dateError}</div>
                    )}
                </>
            )}
          </div>
        </div>
        
        <div className="flex justify-end">
          <button 
            className={`bg-[#036f36] text-white px-4 py-2 rounded hover:bg-[#025828] flex items-center ${isLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
            onClick={handleApplyFilters}
            disabled={isLoading}
          >
            {isLoading ? (
                <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
                </>
            ) : 'Apply Filters'}
          </button>
        </div>
      </div>
      
      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="border border-gray-300 p-4 rounded-lg text-center">
          <h3 className="text-sm font-medium text-gray-500">Total Downloads</h3>
          <p className="text-2xl font-bold text-gray-800">{dashboardData?.total_downloads}</p>
        </div>
        <div className="border border-gray-300 p-4 rounded-lg text-center">
          <h3 className="text-sm font-medium text-gray-500">Avg Downloads/Week</h3>
          <p className="text-2xl font-bold text-gray-800">{dashboardData?.average_downloads_per_week}</p>
        </div>
        <div className="border border-gray-300 p-4 rounded-lg text-center">
          <h3 className="text-sm font-medium text-gray-500">Avg Downloads/Month</h3>
          <p className="text-2xl font-bold text-gray-800">{dashboardData?.average_downloads_per_month}</p>
        </div>
        <div className="border border-gray-300 p-4 rounded-lg text-center">
          <h3 className="text-sm font-medium text-gray-500">Avg Downloads/Year</h3>
          <p className="text-2xl font-bold text-gray-800">{dashboardData?.average_downloads_per_year}</p>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Downloads by Reason */}
        <div className="border border-gray-300 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Downloads by Reason</h2>
          <div className="flex">
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={reasonData}
                  layout="vertical"
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                  barSize={30}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={140} />
                  <Tooltip formatter={(value, name) => [`${value}`, 'Downloads']}/>
                  <Legend />
                  <Bar dataKey="downloads" fill={barColor} name="Number of Downloads" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        
        {/* Downloads by Interest Group */}
        <div className="border border-gray-300 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Downloads by Interest Group</h2>
          <div className="flex justify-center">
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <Pie
                    data={interestGroupData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {interestGroupData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [`${value} downloads`, name]}
                  />
                  <Legend 
                    layout="vertical" 
                    verticalAlign="middle" 
                    align="right"
                    wrapperStyle={{ 
                      width: '200px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tables Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top 5 Most Downloaded Policies */}
        <div className="border border-gray-300 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Top 5 Most Downloaded Policies</h2>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left py-2">#</th>
                <th className="text-left py-2">Policy Name</th>
                <th className="text-right py-2">Download Count</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData?.top_policies.labels?.map((policy, index) => (
                <tr key={`policy-${index}`} className="border-t">
                  <td className="py-2">{index + 1}</td>
                  <td className="py-2">{policy}</td>
                  <td className="text-right py-2">{dashboardData?.top_policies?.counts[index]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Top 5 Most Downloaded Policy-Associated Systems */}
        <div className="border border-gray-300 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Top 5 Most Downloaded Policy-Associated Systems</h2>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="text-left py-2">#</th>
                <th className="text-left py-2">System Name</th>
                <th className="text-right py-2">Download Count</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData?.top_systems.labels?.map((system, index) => (
                <tr key={`system-${index}`} className="border-t">
                  <td className="py-2">{index + 1}</td>
                  <td className="py-2">{system}</td>
                  <td className="text-right py-2">{dashboardData?.top_systems?.counts[index]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PublicPolicyDownloadsDashboard;